import { CaptureUpdateAction, useExcalidrawAPI } from "@excalidraw/excalidraw";
import { useUIAppState } from "@excalidraw/excalidraw/context/ui-appState";
import { redrawStaticScene } from "@excalidraw/excalidraw/renderer/staticScene";
import { arrayToMap, randomId } from "@excalidraw/common";
import {
  getElementsOverlappingFrame,
  isFrameLikeElement,
} from "@excalidraw/element";
import { useEffect, useReducer, useRef, useState } from "react";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import { AnimationRuntime } from "../animation/runtime";
import { getElementAnimation, getScreenDurationMs } from "../animation/types";
import {
  prepareSmartTransition,
  recordScreens,
  renderScreenSnapshot,
} from "../animation/export/record";
import { playTransitionOverlay } from "../animation/transitionOverlay";
import {
  CAMERA_MOVE_LABELS,
  CAMERA_MOVES,
  CAMERA_TRACK_CHANNELS,
  cameraAt,
  getScreenCamera,
  withScreenCamera,
} from "../animation/camera";

import {
  DEFAULT_TRANSITION_MS,
  getScreenTransition,
  TRANSITION_TYPES,
  travelEase,
  travelRectAt,
  withScreenTransition,
} from "../animation/transitions";

import {
  buildExportTargets,
  planScreens,
  resolveTarget,
} from "../animation/export/targets";
import { downloadBlob } from "../animation/export/download";
import { flipbookPageCustomData, getScreens } from "../flipbook/ops";
import { useFlipbook } from "../flipbook/FlipbookContext";

import { KeyframeEditor } from "./KeyframeEditor";

import "./AnimationPanel.scss";

import type {
  ScreenTransition,
  TransitionType,
} from "../animation/transitions";

import type { ExportFormat, ExportQuality } from "../animation/export/encoders";
import type { ExportTargetGroup } from "../animation/export/targets";

/** Hexagon-gear glyph for the per-screen settings disclosure. */
const ScreenSettingsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19.875 6.27A2.23 2.23 0 0 1 21 8.218v7.284c0 .809-.443 1.555-1.158 1.948l-6.75 4.27a2.27 2.27 0 0 1-2.184 0l-6.75-4.27A2.23 2.23 0 0 1 3 15.502V8.217c0-.809.443-1.554 1.158-1.947l6.75-3.98a2.33 2.33 0 0 1 2.25 0l6.75 3.98z" />
    <path d="M9 12a3 3 0 1 0 6 0a3 3 0 1 0-6 0" />
  </svg>
);

/** `<optgroup>` render order; ungrouped targets ("All screens") come first. */
const EXPORT_TARGET_GROUPS: Exclude<ExportTargetGroup, "">[] = [
  "Screens",
  "Flipbooks",
  "Pages",
];

/**
 * Right-sidebar "Settings" tab: screen sequencing, export (GIF/APNG/MP4/WebM),
 * and the play/loop bar. Owns its own AnimationRuntime — playback, sequence
 * play and export are self-contained here and don't depend on the Animation
 * tab's gallery/selection. (Extracted from AnimationPanel.)
 *
 * This is the ONLY export surface: the Target select lists every screen, every
 * flipbook and every flipbook page, and all of them run through one
 * recordScreens call (see animation/export/targets.ts).
 */
export const SettingsPanel = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const appState = useUIAppState();
  const {
    fps: flipbookFps,
    exportTargetId,
    setExportTargetId,
    playing: flipbookPlaying,
    setPlaying: setFlipbookPlaying,
    onion,
    setOnion,
    setOpen: setFlipbookOpen,
    setActiveId: setFlipbookActiveId,
    setActiveBookId,
  } = useFlipbook();
  const runtimeRef = useRef<AnimationRuntime | null>(null);
  const [playState, setPlayState] = useState<"idle" | "playing" | "paused">(
    "idle",
  );
  const isPlaying = playState !== "idle";
  const [loopPlayback, setLoopPlayback] = useState(true);
  const [isSequencePlaying, setIsSequencePlaying] = useState(false);
  // frame id whose keyframed-camera curve editor is open inline
  const [cameraCurvesFor, setCameraCurvesFor] = useState<string | null>(null);
  // frame id whose per-screen settings (transition/camera/flipbook) are open
  const [screenSettingsFor, setScreenSettingsFor] = useState<string | null>(
    null,
  );
  const sequenceCancelRef = useRef(false);
  // cancel for a screen-transition overlay in flight (resolves its promise)
  const transitionCancelRef = useRef<(() => void) | null>(null);
  const [format, setFormat] = useState<ExportFormat>("gif");
  const [fps, setFps] = useState(24);
  // 2x default so GIFs stay sharp when zoomed; 1x pixelates immediately
  const [scale, setScale] = useState(2);
  const [quality, setQuality] = useState<ExportQuality>("medium");
  const [bgMode, setBgMode] = useState<"canvas" | "custom" | "transparent">(
    "canvas",
  );
  const [customBg, setCustomBg] = useState("#ffffff");
  const [loopForever, setLoopForever] = useState(true);
  const [fileName, setFileName] = useState("");
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // This panel derives screens/durations/animatedCount from scene elements, but
  // useUIAppState only re-renders on appState changes. Screen durations and
  // applying presets mutate scene data — so subscribe to scene changes and
  // force a render, otherwise the controlled duration inputs snap back to
  // their old values and the list goes stale.
  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    return excalidrawAPI.onChange(() => forceRender());
  }, [excalidrawAPI]);

  const getRuntime = () => {
    if (!runtimeRef.current) {
      runtimeRef.current = new AnimationRuntime();
    }
    return runtimeRef.current;
  };

  // latest-value ref so the unmount cleanup doesn't close over the
  // first-render (typically null) excalidrawAPI
  const apiRef = useRef(excalidrawAPI);
  apiRef.current = excalidrawAPI;

  // stop playback + release overrides on unmount
  useEffect(() => {
    return () => {
      sequenceCancelRef.current = true;
      runtimeRef.current?.dispose();
      apiRef.current?.triggerRender(true);
    };
  }, []);

  const elements = excalidrawAPI?.getSceneElements() ?? [];
  const selectedElements = elements.filter(
    (el) => appState.selectedElementIds[el.id] && !isFrameLikeElement(el),
  );
  const frames = getScreens(elements);
  const animatedCount = elements.filter((el) => getElementAnimation(el)).length;

  const isVideoFormat = format === "mp4" || format === "webm";
  // rebuilt every render (the panel already force-renders on scene change), so
  // adding/deleting a frame or a book updates the menu with no extra wiring
  const exportTargets = buildExportTargets(elements, flipbookFps);
  const exportTarget = resolveTarget(exportTargets, exportTargetId);
  const exportFrames = exportTarget?.frames ?? [];
  // output dimensions preview — mirrors record.ts's evenFloor
  const even = (n: number) => Math.max(2, Math.floor(n) & ~1);
  const outWidth = exportFrames.length
    ? even(Math.max(...exportFrames.map((f) => f.width)) * scale)
    : 0;
  const outHeight = exportFrames.length
    ? even(Math.max(...exportFrames.map((f) => f.height)) * scale)
    : 0;

  // active screen = last one touched by the selection, else the last screen
  let activeFrameIndex = frames.length - 1;
  for (let i = 0; i < frames.length; i++) {
    if (
      appState.selectedElementIds[frames[i].id] ||
      selectedElements.some((el) => el.frameId === frames[i].id)
    ) {
      activeFrameIndex = i;
    }
  }
  // mutateElement alone never records an undo entry
  const captureUndo = () => {
    excalidrawAPI?.updateScene({
      elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
      captureUpdate: CaptureUpdateAction.IMMEDIATELY,
    });
  };

  const setScreenDuration = (
    frame: typeof frames[number],
    durationMs: number,
    opts: { capture?: boolean } = {},
  ) => {
    excalidrawAPI?.mutateElement(frame as ExcalidrawElement, {
      customData: { ...frame.customData, screenDurationMs: durationMs },
    });
    // capture: false marks a transient mid-edit write — the durable entry
    // comes from the blur/batch commit, so typing "1500" is ONE undo step
    if (opts.capture !== false) {
      captureUndo();
    }
  };

  const setScreenTransitionConfig = (
    frame: typeof frames[number],
    transition: ScreenTransition | null,
  ) => {
    excalidrawAPI?.mutateElement(frame as ExcalidrawElement, {
      customData: withScreenTransition(frame, transition),
    });
    captureUndo();
  };

  const setScreenCameraConfig = (
    frame: typeof frames[number],
    camera: Parameters<typeof withScreenCamera>[1],
  ) => {
    excalidrawAPI?.mutateElement(frame as ExcalidrawElement, {
      customData: withScreenCamera(frame, camera),
    });
    captureUndo();
  };

  const goToScreen = (frame: typeof frames[number]) => {
    excalidrawAPI?.setViewport({
      target: [frame],
      fit: "scale-down",
      animation: true,
    });
  };

  /**
   * "Start flipbook" on a screen row: the screen becomes page 1 of its own new
   * flipbook (a page is just a frame carrying the book marker — see
   * flipbook/ops.ts), which then becomes the active book and opens the bottom
   * filmstrip. Its contents survive, so this is a promotion, not a copy, and
   * undo puts it back in the Screens list.
   */
  const startFlipbook = (frame: typeof frames[number]) => {
    if (!excalidrawAPI) {
      return;
    }
    const bookId = `book-${randomId()}`;
    excalidrawAPI.mutateElement(frame as ExcalidrawElement, {
      customData: flipbookPageCustomData(
        {
          ...frame.customData,
          screenDurationMs: Math.round(1000 / flipbookFps),
        },
        bookId,
      ),
    });
    captureUndo();
    setActiveBookId(bookId);
    setFlipbookActiveId(frame.id);
    setFlipbookOpen(true);
    goToScreen(frame);
  };

  const toggleSequence = async () => {
    if (!excalidrawAPI || progress !== null || isPlaying) {
      return;
    }
    if (isSequencePlaying) {
      sequenceCancelRef.current = true;
      // a transition mid-flight resolves immediately so the loop can exit
      transitionCancelRef.current?.();
      return;
    }
    const runtime = getRuntime();
    // per-tick: direct canvas redraw, not the React setState → throttled-rAF
    // path (costs an extra frame per tick — ~30fps cap)
    const tick = () => redrawStaticScene();
    setIsSequencePlaying(true);
    sequenceCancelRef.current = false;
    setError(null);
    try {
      const sceneElements = excalidrawAPI.getSceneElements();
      const elementsMap = arrayToMap(sceneElements);
      for (let i = 0; i < frames.length; i++) {
        const frame = frames[i];
        if (sequenceCancelRef.current) {
          break;
        }
        // transition INTO this screen (dissolve/wipe/push/iris): composite
        // rest-state snapshots of both screens on an overlay, then cut.
        // Export samples animations precisely; live preview accepts the
        // rest-state approximation to stay cheap.
        const transition = i > 0 ? getScreenTransition(frame) : null;
        if (transition) {
          if (transition.type === "travel") {
            // travel drives the REAL viewport across canvas space — no
            // overlay, content between the screens is genuinely visible
            // mid-flight
            const fromFrame = frames[i - 1];
            await new Promise<void>((resolve) => {
              const start = performance.now();
              const step = () => {
                if (sequenceCancelRef.current) {
                  resolve();
                  return;
                }
                const p = Math.min(
                  1,
                  (performance.now() - start) / transition.durationMs,
                );
                const rect = travelRectAt(fromFrame, frame, travelEase(p));
                const st = excalidrawAPI.getAppState();
                const fit = Math.min(
                  st.width / Math.max(1, rect.width),
                  st.height / Math.max(1, rect.height),
                  1,
                );
                const zoom = Math.min(30, Math.max(0.1, fit));
                excalidrawAPI.updateScene({
                  appState: {
                    scrollX: st.width / (2 * zoom) - (rect.x + rect.width / 2),
                    scrollY:
                      st.height / (2 * zoom) - (rect.y + rect.height / 2),
                    zoom: { value: zoom as typeof st.zoom.value },
                  },
                  captureUpdate: CaptureUpdateAction.NEVER,
                });
                if (p >= 1) {
                  resolve();
                  return;
                }
                requestAnimationFrame(step);
              };
              requestAnimationFrame(step);
            });
          } else {
            const liveAppState = excalidrawAPI.getAppState();
            const bg = liveAppState.viewBackgroundColor;
            let overlayArgs: Parameters<typeof playTransitionOverlay>[0];
            if (transition.type === "smart") {
              // matched elements morph — build the smart draw closure at
              // overlay resolution (same rect math the overlay itself uses)
              const rect = document
                .querySelector(".excalidraw")
                ?.getBoundingClientRect() ?? {
                width: window.innerWidth,
                height: window.innerHeight,
              };
              const dpr = window.devicePixelRatio || 1;
              const w = Math.max(2, Math.round(rect.width * dpr));
              const h = Math.max(2, Math.round(rect.height * dpr));
              const fit = Math.min(
                w / Math.max(1, Math.max(frames[i - 1].width, frame.width)),
                h / Math.max(1, Math.max(frames[i - 1].height, frame.height)),
                dpr,
              );
              const draw = await prepareSmartTransition({
                elements: sceneElements,
                appState: liveAppState,
                files: excalidrawAPI.getFiles(),
                fromFrame: frames[i - 1],
                toFrame: frame,
                scale: fit,
                width: w,
                height: h,
                background: bg,
              });
              overlayArgs = {
                transition,
                background: bg,
                draw,
                onDone: () => {},
              };
            } else {
              const [fromCanvas, toCanvas] = await Promise.all([
                renderScreenSnapshot({
                  elements: sceneElements,
                  appState: liveAppState,
                  files: excalidrawAPI.getFiles(),
                  frame: frames[i - 1],
                  background: bg,
                }),
                renderScreenSnapshot({
                  elements: sceneElements,
                  appState: liveAppState,
                  files: excalidrawAPI.getFiles(),
                  frame,
                  background: bg,
                }),
              ]);
              overlayArgs = {
                from: fromCanvas,
                to: toCanvas,
                transition,
                background: bg,
                onDone: () => {},
              };
            }
            if (sequenceCancelRef.current) {
              break;
            }
            await new Promise<void>((resolve) => {
              transitionCancelRef.current = playTransitionOverlay({
                ...overlayArgs,
                onDone: resolve,
              });
            });
            transitionCancelRef.current = null;
            if (sequenceCancelRef.current) {
              break;
            }
          }
        }
        if (sequenceCancelRef.current) {
          break;
        }
        const frameElements = getElementsOverlappingFrame(
          sceneElements,
          frame,
          elementsMap,
        );
        // finite compile (repeat -1 → one cycle): the screen advances on its
        // own clock, so an infinite tween must not stretch getDurationMs()
        const count = runtime.compile(frameElements);
        const effectiveMs = Math.max(
          getScreenDurationMs(frame),
          runtime.getDurationMs(),
        );
        const camera = getScreenCamera(frame);
        if (camera) {
          // drive the REAL viewport from the same numbers export uses —
          // element animations keep playing under the camera
          if (count > 0) {
            runtime.play(tick);
          }
          await new Promise<void>((resolve) => {
            const start = performance.now();
            const step = () => {
              if (sequenceCancelRef.current) {
                resolve();
                return;
              }
              const p = Math.min(1, (performance.now() - start) / effectiveMs);
              const t = cameraAt(camera, p);
              const st = excalidrawAPI.getAppState();
              const fit = Math.min(
                st.width / Math.max(1, frame.width),
                st.height / Math.max(1, frame.height),
                1,
              );
              const zoom = Math.min(30, Math.max(0.1, fit * t.zoom));
              excalidrawAPI.updateScene({
                appState: {
                  scrollX:
                    st.width / (2 * zoom) -
                    (frame.x + frame.width / 2 + t.dx * frame.width),
                  scrollY:
                    st.height / (2 * zoom) -
                    (frame.y + frame.height / 2 + t.dy * frame.height),
                  zoom: { value: zoom as typeof st.zoom.value },
                },
                captureUpdate: CaptureUpdateAction.NEVER,
              });
              if (p >= 1) {
                resolve();
                return;
              }
              requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          });
        } else {
          excalidrawAPI.setViewport({
            target: [frame],
            fit: "scale-down",
            // flipbook-rate screens must cut, not pan (the pan would eat the
            // whole frame time); a screen entered via a transition also cuts —
            // the overlay already animated the move
            animation: effectiveMs >= 500 && !transition,
          });
          if (count > 0) {
            runtime.play(tick);
          }
          await new Promise((resolve) => setTimeout(resolve, effectiveMs));
        }
        if (runtime.isPlaying) {
          runtime.stop(tick);
        }
      }
      // a camera screen leaves the viewport zoomed/panned — settle it back
      if (frames.length > 0 && getScreenCamera(frames[frames.length - 1])) {
        excalidrawAPI.setViewport({
          target: [frames[frames.length - 1]],
          fit: "scale-down",
          animation: true,
        });
      }
    } finally {
      if (runtime.isPlaying) {
        runtime.stop(tick);
      }
      setIsSequencePlaying(false);
    }
  };

  const onPlayPause = () => {
    if (progress !== null || isSequencePlaying) {
      // preview would clobber the export/sequence runtime's global overrides
      return;
    }
    const runtime = getRuntime();
    // per-tick: direct canvas redraw (see the sequence tick above)
    const tick = () => redrawStaticScene();
    if (playState === "playing") {
      runtime.pause();
      setPlayState("paused");
      return;
    }
    if (playState === "paused") {
      runtime.resume();
      setPlayState("playing");
      return;
    }
    const count = runtime.compile(excalidrawAPI?.getSceneElements() ?? []);
    if (count === 0) {
      setError("Nothing to play — select elements and apply a preset first.");
      return;
    }
    setError(null);
    setPlayState("playing");
    runtime.play(
      () => {
        tick();
        if (!runtime.isPlaying) {
          setPlayState("idle");
          excalidrawAPI?.triggerRender(true);
        }
      },
      { loop: loopPlayback },
    );
  };

  const onStopPlayback = () => {
    const runtime = getRuntime();
    if (runtime.isPlaying) {
      runtime.stop(() => excalidrawAPI?.triggerRender(true));
    }
    setPlayState("idle");
  };

  const onExport = async () => {
    if (!excalidrawAPI) {
      return;
    }
    if (!exportTarget) {
      setError(
        "Add a screen or a flipbook page first — there is nothing to export.",
      );
      return;
    }
    if (isSequencePlaying) {
      // the sequence runtime and the export runtime would fight over the
      // single global overrides slot — frames would bake in sequence state
      return;
    }
    const runtime = getRuntime();
    if (runtime.isPlaying) {
      runtime.stop(() => excalidrawAPI.triggerRender(true));
      setPlayState("idle");
    }
    // the export runtime, flipbook playback and onion-skin all write the same
    // global animation-overrides slot (see animationOverrides.ts) — stop
    // playback and suspend ghosts for the duration or they fight over
    // ownership mid-render and bake ghost state into the output
    if (flipbookPlaying) {
      setFlipbookPlaying(false);
    }
    const restoreOnion = onion.enabled;
    if (restoreOnion) {
      setOnion((current) => ({ ...current, enabled: false }));
    }
    setError(null);
    setProgress({ done: 0, total: 1 });
    try {
      const blob = await recordScreens({
        elements: excalidrawAPI.getSceneElements(),
        appState: excalidrawAPI.getAppState(),
        files: excalidrawAPI.getFiles(),
        screens: planScreens(exportTarget),
        format,
        fps,
        scale,
        background:
          bgMode === "transparent"
            ? "transparent"
            : bgMode === "custom"
            ? customBg
            : undefined,
        loopForever,
        quality,
        onProgress: (done: number, total: number) =>
          setProgress({ done, total }),
      });
      // .png so every viewer opens APNG files (they are valid PNGs)
      const ext = format === "apng" ? "png" : format;
      const base =
        fileName.trim().replace(/[\\/:*?"<>|]/g, "") || exportTarget.fileBase;
      downloadBlob(blob, `${base}.${ext}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setProgress(null);
      if (restoreOnion) {
        setOnion((current) => ({ ...current, enabled: true }));
      }
      excalidrawAPI.triggerRender(true);
    }
  };

  return (
    <div className="animation-panel">
      <section>
        <h3>Screens</h3>
        <p className="animation-panel__hint">
          {frames.length === 0
            ? "Add a screen (top-left menu) to get started."
            : "The sequence plays screens in order. Click a name to jump to it."}
        </p>
        {frames.map((frame, index) => {
          const transition = getScreenTransition(frame);
          const camera = getScreenCamera(frame);
          return (
            <div className="animation-panel__screen" key={frame.id}>
              <div className="animation-panel__screen-main">
                <button
                  className="animation-panel__screen-name"
                  title="Go to this screen"
                  onClick={() => goToScreen(frame)}
                >
                  {index === activeFrameIndex ? "▸ " : ""}
                  {frame.name || `Screen ${index + 1}`}
                </button>
                <input
                  type="number"
                  min={33}
                  max={60000}
                  step={1}
                  title="Screen duration (ms)"
                  value={getScreenDurationMs(frame)}
                  onChange={(e) => {
                    // never commit a cleared field or NaN mid-edit; typed values
                    // outside min/max are clamped on blur
                    const value = Number(e.target.value);
                    if (e.target.value !== "" && Number.isFinite(value)) {
                      setScreenDuration(frame, value, { capture: false });
                    }
                  }}
                  onBlur={(e) =>
                    setScreenDuration(
                      frame,
                      Math.min(
                        60000,
                        Math.max(33, Number(e.target.value) || 33),
                      ),
                    )
                  }
                />
                <button
                  className={`animation-panel__screen-settings${
                    screenSettingsFor === frame.id
                      ? " animation-panel__screen-settings--open"
                      : ""
                  }${
                    transition || camera
                      ? " animation-panel__screen-settings--configured"
                      : ""
                  }`}
                  title="Screen settings — transition, camera, flipbook"
                  aria-label={`Settings for ${
                    frame.name || `Screen ${index + 1}`
                  }`}
                  aria-expanded={screenSettingsFor === frame.id}
                  onClick={() =>
                    setScreenSettingsFor(
                      screenSettingsFor === frame.id ? null : frame.id,
                    )
                  }
                >
                  <ScreenSettingsIcon />
                </button>
              </div>
              {/* disclosure: transition + camera + flipbook, HIG-style inset
                  card — rows stay name/time-only until the gear opens them */}
              {screenSettingsFor === frame.id && (
                <div className="animation-panel__screen-fx">
                  {index > 0 && (
                    <span
                      className="animation-panel__fx-group"
                      title="Transition into this screen"
                    >
                      <span className="animation-panel__fx-label">In</span>
                      <select
                        className="animation-panel__transition"
                        title="How this screen enters (transition from the previous screen)"
                        value={transition?.type ?? "cut"}
                        onChange={(e) =>
                          setScreenTransitionConfig(frame, {
                            type: e.target.value as TransitionType,
                            durationMs:
                              transition?.durationMs ?? DEFAULT_TRANSITION_MS,
                            direction: transition?.direction ?? 0,
                          })
                        }
                      >
                        {TRANSITION_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t[0].toUpperCase() + t.slice(1)}
                          </option>
                        ))}
                      </select>
                      {transition &&
                        (transition.type === "wipe" ||
                          transition.type === "push") && (
                          <select
                            className="animation-panel__transition"
                            title="Transition direction"
                            value={transition.direction ?? 0}
                            onChange={(e) =>
                              setScreenTransitionConfig(frame, {
                                ...transition,
                                direction: Number(e.target.value) as
                                  | 0
                                  | 1
                                  | 2
                                  | 3,
                              })
                            }
                          >
                            {["← Left", "→ Right", "↑ Up", "↓ Down"].map(
                              (label, i) => (
                                <option key={label} value={i}>
                                  {label}
                                </option>
                              ),
                            )}
                          </select>
                        )}
                      {transition && (
                        <input
                          type="number"
                          className="animation-panel__transition-ms"
                          min={100}
                          max={5000}
                          step={100}
                          title="Transition duration (ms)"
                          value={transition.durationMs}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (
                              e.target.value !== "" &&
                              Number.isFinite(value)
                            ) {
                              setScreenTransitionConfig(frame, {
                                ...transition,
                                durationMs: value,
                              });
                            }
                          }}
                        />
                      )}
                    </span>
                  )}
                  <span
                    className="animation-panel__fx-group"
                    title="Camera move over this screen's duration"
                  >
                    <span className="animation-panel__fx-label">Cam</span>
                    <select
                      className="animation-panel__transition"
                      title="Camera move over this screen's duration"
                      value={camera?.move ?? ""}
                      onChange={(e) =>
                        setScreenCameraConfig(
                          frame,
                          e.target.value
                            ? {
                                move: e.target
                                  .value as typeof CAMERA_MOVES[number],
                                amount: camera?.amount ?? 0.25,
                              }
                            : null,
                        )
                      }
                    >
                      <option value="">No camera</option>
                      {CAMERA_MOVES.map((move) => (
                        <option key={move} value={move}>
                          {CAMERA_MOVE_LABELS[move]}
                        </option>
                      ))}
                    </select>
                    {camera && camera.move !== "keyframed" && (
                      <input
                        type="number"
                        className="animation-panel__transition-ms"
                        min={5}
                        max={100}
                        step={5}
                        title="Camera amount (%)"
                        value={Math.round(camera.amount * 100)}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (e.target.value !== "" && Number.isFinite(value)) {
                            setScreenCameraConfig(frame, {
                              ...camera,
                              amount: value / 100,
                            });
                          }
                        }}
                      />
                    )}
                    {camera?.move === "keyframed" && (
                      <button
                        className="animation-panel__transition"
                        title="Edit the camera's zoom/pan curves"
                        onClick={() =>
                          setCameraCurvesFor(
                            cameraCurvesFor === frame.id ? null : frame.id,
                          )
                        }
                      >
                        Curves
                      </button>
                    )}
                  </span>
                  <button
                    className="animation-panel__fx-flipbook"
                    title="Start a flipbook from this screen — it becomes page 1 of a new flipbook"
                    onClick={() => startFlipbook(frame)}
                  >
                    🎞 Start flipbook
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {frames.map((frame) => {
          const camera = getScreenCamera(frame);
          if (cameraCurvesFor !== frame.id || camera?.move !== "keyframed") {
            return null;
          }
          return (
            <KeyframeEditor
              key={`cam-${frame.id}`}
              tracks={camera.keyframes ?? {}}
              durationMs={getScreenDurationMs(frame)}
              channels={CAMERA_TRACK_CHANNELS}
              onChange={(keyframes) =>
                setScreenCameraConfig(frame, { ...camera, keyframes })
              }
              onClose={() => setCameraCurvesFor(null)}
            />
          );
        })}
        {frames.length > 0 && (
          <button
            onClick={toggleSequence}
            disabled={progress !== null || isPlaying}
          >
            {isSequencePlaying
              ? "Stop sequence"
              : `Play sequence (${frames.length} screens)`}
          </button>
        )}
      </section>

      <section>
        <h3>Export</h3>
        <p className="animation-panel__hint">
          Export any screen, any flipbook, or a single page — exactly as it is.
          Screens run for their own duration; flipbook pages run at the
          flipbook&apos;s {flipbookFps} fps.
        </p>
        <button
          className="animation-panel__studio-open"
          data-testid="open-studio"
          title="Open this board's sequence editor in a new tab — screen cards, timeline, transitions, trim"
          onClick={() => {
            const url = new URL(window.location.href);
            url.searchParams.set("studio", "1");
            const opened = window.open(url.toString(), "_blank", "noopener");
            if (!opened) {
              // popup blocked → open Studio right here instead of silently
              // doing nothing
              window.dispatchEvent(new CustomEvent("chartoon:studio-open"));
            }
          }}
        >
          ◤ Open Studio ↗
        </button>
        <label title="What to export: a whole sequence, one screen, a flipbook, or a single page">
          Target
          <select
            className="animation-panel__export-target"
            value={exportTarget?.id ?? ""}
            disabled={exportTargets.length === 0}
            onChange={(e) => setExportTargetId(e.target.value)}
          >
            {exportTargets.length === 0 ? (
              <option value="">Nothing to export</option>
            ) : (
              <>
                {exportTargets
                  .filter((target) => !target.group)
                  .map((target) => (
                    <option key={target.id} value={target.id}>
                      {target.label}
                    </option>
                  ))}
                {EXPORT_TARGET_GROUPS.map((group) => {
                  const items = exportTargets.filter(
                    (target) => target.group === group,
                  );
                  return items.length === 0 ? null : (
                    <optgroup key={group} label={group}>
                      {items.map((target) => (
                        <option key={target.id} value={target.id}>
                          {target.label}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </>
            )}
          </select>
        </label>
        <label>
          Format
          <select
            value={format}
            onChange={(e) => {
              const next = e.target.value as ExportFormat;
              setFormat(next);
              // transparency only survives the APNG encoder
              if (next !== "apng" && bgMode === "transparent") {
                setBgMode("canvas");
              }
            }}
          >
            <option value="gif">GIF</option>
            <option value="apng">APNG (lossless)</option>
            <option value="mp4">MP4</option>
            <option value="webm">WebM</option>
          </select>
        </label>
        <label title="Frames per second (1–60)">
          FPS
          <input
            type="number"
            min={1}
            max={60}
            step={1}
            value={fps}
            onChange={(e) => {
              const value = Number(e.target.value);
              if (e.target.value !== "" && Number.isFinite(value)) {
                setFps(value);
              }
            }}
            onBlur={(e) =>
              setFps(Math.min(60, Math.max(1, Number(e.target.value) || 24)))
            }
          />
        </label>
        <label>
          Scale
          <select
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
          >
            <option value={0.5}>0.5x</option>
            <option value={1}>1x</option>
            <option value={1.5}>1.5x</option>
            <option value={2}>2x</option>
            <option value={3}>3x</option>
            <option value={4}>4x</option>
          </select>
        </label>
        {isVideoFormat && (
          <label title="Video bitrate — higher is crisper but larger">
            Quality
            <select
              value={quality}
              onChange={(e) => setQuality(e.target.value as ExportQuality)}
            >
              <option value="low">Low (small file)</option>
              <option value="medium">Medium</option>
              <option value="high">High (crisp)</option>
            </select>
          </label>
        )}
        <label>
          Background
          <select
            value={bgMode}
            onChange={(e) => setBgMode(e.target.value as typeof bgMode)}
          >
            <option value="canvas">Canvas color</option>
            <option value="custom">Custom color</option>
            <option value="transparent" disabled={format !== "apng"}>
              Transparent (APNG only)
            </option>
          </select>
        </label>
        {bgMode === "custom" && (
          <label>
            Color
            <input
              type="color"
              value={customBg}
              onChange={(e) => setCustomBg(e.target.value)}
            />
          </label>
        )}
        {!isVideoFormat && (
          <label title="Video files always play once — players handle looping">
            Loop
            <select
              value={loopForever ? "forever" : "once"}
              onChange={(e) => setLoopForever(e.target.value === "forever")}
            >
              <option value="forever">Forever</option>
              <option value="once">Play once</option>
            </select>
          </label>
        )}
        <label>
          File name
          <input
            type="text"
            value={fileName}
            placeholder={exportTarget?.fileBase ?? "animation"}
            onChange={(e) => setFileName(e.target.value)}
          />
        </label>
        {exportFrames.length > 0 && (
          <p className="animation-panel__hint animation-panel__output">
            Output: {outWidth}×{outHeight}px · {fps} fps ·{" "}
            {format.toUpperCase()} ·{" "}
            {exportFrames.length === 1
              ? "1 frame"
              : `${exportFrames.length} frames`}
          </p>
        )}
        <button
          className="animation-panel__cta"
          onClick={onExport}
          disabled={
            progress !== null ||
            // the sequence runtime and an export would fight over the single
            // global overrides slot — block until the sequence stops
            isSequencePlaying ||
            // a static target is a legitimate export ("as it is"), so the only
            // hard requirement is having something to render
            exportTarget === null
          }
        >
          {progress
            ? `Exporting… ${Math.round(
                (progress.done / progress.total) * 100,
              )}%`
            : "Export"}
        </button>
        {progress && <progress value={progress.done} max={progress.total} />}
      </section>

      {error && <p className="animation-panel__error">{error}</p>}

      <div className="animation-panel__playbar">
        <label
          className="animation-panel__inline"
          title="Sprite-style loop until stopped"
        >
          <input
            type="checkbox"
            checked={loopPlayback}
            onChange={(e) => setLoopPlayback(e.target.checked)}
          />
          Loop
        </label>
        <button
          className="animation-panel__playbar-main"
          onClick={onPlayPause}
          disabled={
            animatedCount === 0 || progress !== null || isSequencePlaying
          }
        >
          {playState === "playing"
            ? "❚❚ Pause"
            : playState === "paused"
            ? "▶ Resume"
            : `▶ Play (${animatedCount})`}
        </button>
        <button
          onClick={onStopPlayback}
          disabled={playState === "idle"}
          title="Stop and reset"
        >
          ■
        </button>
      </div>
    </div>
  );
};
